import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Send, CheckCircle, Loader2, Upload, FileText, X } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const roles = [
  "Interim CISO",
  "Security Architect",
  "GRC Analyst",
  "Compliance Consultant",
  "Program Manager",
  "Security Engineer",
  "Risk Analyst",
  "Vulnerability Management Analyst",
  "AI Governance Specialist",
  "Other",
];

export default function CareersPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [resume, setResume] = useState(null);
  const [coverLetter, setCoverLetter] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    linkedin: '',
    message: '',
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (type, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be under 10MB.');
        return;
      }
      if (type === 'resume') setResume(file);
      if (type === 'coverLetter') setCoverLetter(file);
      setError('');
    }
  };

  const removeFile = (type) => {
    if (type === 'resume') setResume(null);
    if (type === 'coverLetter') setCoverLetter(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const data = new FormData();
      Object.entries(formData).forEach(([key, val]) => data.append(key, val));
      if (resume) data.append('resume', resume);
      if (coverLetter) data.append('coverLetter', coverLetter);

      await axios.post(`${API}/careers/apply`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly at info@cisodynamics.com.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <section className="bg-[#0B1D33] pt-32 pb-20 lg:pt-36 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.3em] mb-4">
            Join Our Team
          </p>
          <h1 className="text-3xl md:text-4xl font-light text-white mb-5 tracking-tight">
            Careers
          </h1>
          <p className="text-slate-300/80 text-lg max-w-2xl leading-relaxed">
            We are always looking for experienced cybersecurity professionals who share our
            commitment to excellence, integrity, and client impact.
          </p>
        </div>
      </section>

      {/* Why Join */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <Reveal>
              <div>
                <h2 className="text-2xl font-light text-[#0B1D33] mb-6 tracking-tight">
                  Why CISO Dynamics
                </h2>
                <div className="space-y-5 text-slate-500 leading-relaxed">
                  <p>
                    At CISO Dynamics, you will work alongside experienced security leaders on
                    engagements that shape the cybersecurity posture of major organizations across
                    healthcare, financial services, government, and enterprise sectors.
                  </p>
                  <p>
                    We value strategic thinking, professional integrity, and a genuine commitment
                    to our clients' success. If you bring deep expertise and a consulting mindset,
                    we offer the platform to make a meaningful impact.
                  </p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="bg-[#F8FAFC] rounded-lg border border-slate-200/80 p-8">
                <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 mb-6">
                  What We Offer
                </h3>
                <ul className="space-y-4">
                  {[
                    "Challenging engagements with enterprise clients",
                    "Collaborative, senior-level advisory teams",
                    "Professional development and certification support",
                    "Flexible work arrangements",
                    "Competitive compensation and benefits",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#3B82A0] mt-2 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="bg-[#F8FAFC] py-20 lg:py-28 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl mb-12">
              <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.25em] mb-3">
                Apply Now
              </p>
              <h2 className="text-2xl font-light text-[#0B1D33] mb-5 tracking-tight">
                Submit Your Application
              </h2>
              <p className="text-slate-500 leading-relaxed">
                Complete the form below and attach your resume and cover letter.
                Our team reviews every application and will respond within five business days.
              </p>
            </div>
          </Reveal>

          <Reveal delay={100}>
            {submitted ? (
              <div className="bg-white rounded-lg border border-slate-200/80 p-10 lg:p-14 text-center max-w-2xl">
                <div className="w-14 h-14 rounded-full bg-[#3B82A0]/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-7 w-7 text-[#3B82A0]" />
                </div>
                <h3 className="text-xl font-light text-[#0B1D33] mb-3 tracking-tight">
                  Application Received
                </h3>
                <p className="text-slate-500 max-w-md mx-auto leading-relaxed">
                  Thank you for your interest in CISO Dynamics. Our team will review your
                  application and respond within five business days.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-lg border border-slate-200/80 p-8 lg:p-10 max-w-3xl"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      First Name *
                    </Label>
                    <Input
                      required
                      value={formData.firstName}
                      onChange={(e) => handleChange('firstName', e.target.value)}
                      className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20"
                      placeholder="First name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Last Name *
                    </Label>
                    <Input
                      required
                      value={formData.lastName}
                      onChange={(e) => handleChange('lastName', e.target.value)}
                      className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20"
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Email *
                    </Label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20"
                      placeholder="you@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Phone
                    </Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20"
                      placeholder="Phone number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Role of Interest *
                    </Label>
                    <Select
                      value={formData.role}
                      onValueChange={(val) => handleChange('role', val)}
                      required
                    >
                      <SelectTrigger className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((role) => (
                          <SelectItem key={role} value={role}>
                            {role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      LinkedIn Profile
                    </Label>
                    <Input
                      value={formData.linkedin}
                      onChange={(e) => handleChange('linkedin', e.target.value)}
                      className="h-11 border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20"
                      placeholder="linkedin.com/in/yourprofile"
                    />
                  </div>
                </div>

                {/* File Uploads */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Resume *
                    </Label>
                    {resume ? (
                      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5">
                        <FileText className="h-4 w-4 text-[#3B82A0] flex-shrink-0" />
                        <span className="text-sm text-slate-600 truncate flex-1">{resume.name}</span>
                        <button type="button" onClick={() => removeFile('resume')} className="text-slate-400 hover:text-slate-600">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 bg-white border border-slate-200 border-dashed rounded-md px-3 py-2.5 cursor-pointer hover:border-[#3B82A0]/40 transition-colors">
                        <Upload className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-400">Upload resume (PDF, DOC)</span>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          onChange={(e) => handleFileChange('resume', e)}
                        />
                      </label>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Cover Letter
                    </Label>
                    {coverLetter ? (
                      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5">
                        <FileText className="h-4 w-4 text-[#3B82A0] flex-shrink-0" />
                        <span className="text-sm text-slate-600 truncate flex-1">{coverLetter.name}</span>
                        <button type="button" onClick={() => removeFile('coverLetter')} className="text-slate-400 hover:text-slate-600">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 bg-white border border-slate-200 border-dashed rounded-md px-3 py-2.5 cursor-pointer hover:border-[#3B82A0]/40 transition-colors">
                        <Upload className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-400">Upload cover letter (PDF, DOC)</span>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          onChange={(e) => handleFileChange('coverLetter', e)}
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="space-y-2 mb-8">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Additional Information
                  </Label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="min-h-[100px] border-slate-200 focus:border-[#3B82A0] focus:ring-[#3B82A0]/20 resize-none"
                    placeholder="Tell us about your experience and what interests you about CISO Dynamics..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#0B1D33] hover:bg-[#162D4A] text-white px-8 h-11 text-sm font-medium rounded transition-colors duration-200 disabled:opacity-70"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <Send className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
                {error && (
                  <p className="text-sm text-red-500 mt-3">{error}</p>
                )}
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
